import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Typography, TextField, Grid, Button, useMediaQuery } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Formik, Form, Field } from 'formik';

// CONSTANTS
import { contentStatuses } from '@/constants/watchList';

// UTILITY
import setYupValidationSchema from '@/utility/validationSchema/validationSchema';

// COMPONENTS
import StageCarousel from '@/components/Carousels/StageSwiperCarousel';
import { SwiperSlide } from '@/components/Carousels/SwiperCarousel';

// STORE
import { MovieDetailsActions } from '@/store/actions';

// HOOKS
import useActions from '@/hooks/useActions';

// HELP
import ServiceCard from './help/ServiceCard';

// STYLES
import { Container } from './styles';

const initialFormData = {
  email: '',
};

const carouselOptions = {
  spaceBetween: 20,
  pagination: { clickable: true },
  watchOverflow: true,
  autoHeight: true,
  breakpoints: {
    0: {
      speed: 600,
      slidesPerView: 2,
      slidesPerGroup: 1,
    },
    600: {
      speed: 600,
      slidesPerView: 3,
      slidesPerGroup: 3,
    },
    750: {
      slidesPerView: 4,
      slidesPerGroup: 4,
    },
  },
};

const WhereToWatch = props => {
  const {
    type,
    content,
    data,
    className,
    seasons,
    curSeason,
    analyticsLocation,
    subscribed,
    changeContentStatus,
  } = props;
  const [t] = useTranslation();
  const { unique_id, anonymousId, user, isAuth, partner, loading } = useSelector(
    ({ auth, partner: partnerStore, showDetails, movieDetails }) => ({
      unique_id: auth.unique_id,
      anonymousId: auth.anonymousId,
      user: auth.user,
      isAuth: auth.isAuth,
      partner: partnerStore.data,
      loading: showDetails.loadings.subscribe || movieDetails.loadings.subscribe,
    }),
  );
  const [subscribeToChanges] = useActions([
    (submitData, callback) => {
      return MovieDetailsActions.subscribeToChanges.action({ data: submitData, callback });
    },
  ]);

  const downXs = useMediaQuery(({ breakpoints }) => breakpoints.down('xs'));

  function handleSubmit(values, { resetForm }) {
    subscribeToChanges({ email: values.email, content_id: content.id }, resetForm);
  }

  const isAllSeasons = curSeason === 'all';

  function getLiveSeasonLabel(seasonData) {
    if (!seasonData) return null;

    const sources = seasonData.sources && seasonData.sources.map(source => source.title).join(', ');

    return (
      <Typography className="where-to-watch__live-title text-color--purple">
        {`${t('common.season')} ${seasonData.season_number} ${t('common.liveOn')} ${sources}`}
      </Typography>
    );
  }

  const liveSeason = seasons.find(season => season.live);
  const liveSeasonLabel = getLiveSeasonLabel(liveSeason);

  return (
    <Container className={clsx('where-to-watch', className)}>
      {!!liveSeason && liveSeasonLabel}
      {((data.length && type === 'movie') || type === 'show') && (
        <div className="where-to-watch__title">
          <Typography variant="h5">{t('whereToWatch.title')}</Typography>
          {!!seasons.length && (
            <Typography className="where-to-watch__seasons-title" variant="body2">
              {t('showDetails.seasonsAvailability')}
            </Typography>
          )}
        </div>
      )}
      {data.length ? (
        <StageCarousel carouselOptions={carouselOptions} stylesType="where-to-watch">
          {data.map((appData, index) => {
            return (
              <SwiperSlide key={index}>
                <ServiceCard
                  isAllSeasons={isAllSeasons}
                  type={type}
                  data={appData}
                  partner={partner}
                  unique_id={unique_id}
                  anonymousId={anonymousId}
                  fullHeight
                  key={index}
                  location={analyticsLocation || `Slot ${index + 1}`}
                  analyticsData={{
                    content_id: content.content_id || content.id,
                    content_title: content.content_title || content.title,
                    slot: index + 1,
                  }}
                  onWatchNowClick={() => {
                    const { id, user_content_status } = content;
                    const { currentlyWatching } = contentStatuses;

                    const needChangeWatchListStatus = user_content_status !== currentlyWatching;

                    if (needChangeWatchListStatus) {
                      changeContentStatus(id, currentlyWatching);
                    }
                  }}
                />
              </SwiperSlide>
            );
          })}
        </StageCarousel>
      ) : (
        <Alert
          className={clsx('where-to-watch__notify-me', 'notify-me')}
          severity={subscribed ? 'success' : 'info'}
        >
          <AlertTitle>
            {t(
              subscribed
                ? 'whereToWatch.unfortunatelyMessageSuccess'
                : 'whereToWatch.unfortunatelyMessage',
              { content_title: content.content_title },
            )}
          </AlertTitle>

          <Formik
            initialValues={initialFormData}
            validationSchema={setYupValidationSchema('emailSchema', t)}
            onSubmit={handleSubmit}
          >
            {({ values, isValid, setFieldValue }) => {
              if (isAuth && user && user.email !== values.email) {
                setFieldValue('email', user.email);
              }

              return (
                <Form className="notify-me__form">
                  <Grid
                    container
                    spacing={2}
                    justifyContent="center"
                    wrap={downXs ? 'wrap' : 'nowrap'}
                  >
                    {!isAuth && (
                      <Grid item xs={12}>
                        <Field name="email">
                          {({ field, meta }) => {
                            return (
                              <TextField
                                {...field}
                                className="notify-me__form-input"
                                fullWidth
                                placeholder={t('inputs.yourMailPlaceholder')}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error}
                                disabled={loading}
                              />
                            );
                          }}
                        </Field>
                      </Grid>
                    )}
                    <Grid item>
                      <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        disabled={!isValid || loading}
                        className="notify-me__form-button"
                      >
                        {subscribed ? t('buttons.unsubscribe') : t('buttons.notifyMe')}
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </Alert>
      )}
    </Container>
  );
};

WhereToWatch.defaultProps = {
  type: 'movie',
  seasons: [],
  curSeason: 1,
  analyticsLocation: '',
};

WhereToWatch.propTypes = {
  content: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  className: PropTypes.string,
  type: PropTypes.string,
  seasons: PropTypes.array,
  curSeason: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  analyticsLocation: PropTypes.string,
  subscribed: PropTypes.bool.isRequired,
  changeContentStatus: PropTypes.func.isRequired,
};

export default React.memo(WhereToWatch);
